
import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { ModuleBase } from '@loanbossinc/component-library';
import { withRouter } from "react-router";
import { PaidLoanTable } from "./paidLoanTable";
import { TerminatedHedgeTable } from "./terminatedHedgeTable";
import { StyledModuleFooter } from "component-lib/ModuleFooter";
import { pluralizeHedge, pluralizeLoan } from "helpers/pluralize-words";
import {
  mapModulePermissions
  , paidLoanGetDeal
  , paidLoanGetPaidLoansByDeal
  , paidLoanGetTerminatedHedgesByDeal
} from './paidLoan.store';
import {
  SecondaryButton
  , DefaultModuleHeader
  , ButtonSizeOptions
  , ButtonText
  , ICONS
} from '@loanbossinc/component-library';

const PAID_LOAN_STATE = 'PAID_LOAN_STATE';
const TERMINATED_HEDGE_STATE = 'TERMINATED_HEDGE_STATE';

class PaidLoanModule extends React.Component {
  state = {
    currentState: null
    , isExpanded: false
  };

  componentDidMount() {
    const { dealId } = this.props.match.params;
    this.props.paidLoanGetDeal({ dealId });
    this.props.paidLoanGetPaidLoansByDeal({ dealId });
    this.props.paidLoanGetTerminatedHedgesByDeal({ dealId });
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) this.handleStateUpdate();
  }

  handleStateUpdate = () => {
    const { loans, hedges } = this.props;
    const hasLoans = loans && !!loans.length;
    const hasHedges = hedges && !!hedges.length;
    const switchKey = `${+hasLoans}-${+hasHedges}`;

    switch (switchKey) {
      case '0-1':
        this.setState({ currentState: TERMINATED_HEDGE_STATE });
        break;

      case '1-0':
      case '1-1':
      default:
        this.setState({ currentState: PAID_LOAN_STATE });
        break;
    }
  }

  toggleIsExpanded = () => this.setState({ ...this.state, isExpanded: !this.state.isExpanded });
  toggleCurrentState = () => {
    const currentState = this.state.currentState === TERMINATED_HEDGE_STATE ? PAID_LOAN_STATE : TERMINATED_HEDGE_STATE;
    this.setState({
      ...this.state
      , currentState
      , isExpanded: false
    });
  }

  getHeaderButton = () => {
    const { currentState } = this.state;
    const { loans, hedges } = this.props;

    // if we are missing either loans or hedges dont show the button
    const hasTwoStates = loans && loans.length && hedges && hedges.length;
    if (!hasTwoStates) return null;

    const label = currentState === TERMINATED_HEDGE_STATE ? 'Show Loans' : 'Show Hedges';

    return (
      <SecondaryButton
        label={label}
        size={ButtonSizeOptions.smFluid}
        onClick={this.toggleCurrentState}
      />
    );
  }

  getTitle = () => {
    const { currentState } = this.state;
    const { loans, hedges } = this.props;

    const pluralizedLoan = pluralizeLoan(loans);
    const pluralizedHedge = pluralizeHedge(hedges);

    const label = currentState === TERMINATED_HEDGE_STATE ? `Terminated ${pluralizedHedge}` : `Paid off ${pluralizedLoan}`;
    const list = currentState === TERMINATED_HEDGE_STATE ? hedges : loans;

    return `${list.length} ${label}`;
  }

  renderPaidLoanTable = () => {
    const { loans, history } = this.props;
    const { currentState, isExpanded } = this.state;

    if (currentState !== PAID_LOAN_STATE) return null;

    const data = isExpanded ? loans : loans.slice(0, 3);
    return <PaidLoanTable history={history} data={data} />;
  }

  renderTerminatedHedgeTable = () => {
    const { hedges, history } = this.props;
    const { currentState, isExpanded } = this.state;
    const { dealId } = this.props.match.params;

    if (currentState !== TERMINATED_HEDGE_STATE) return null;

    const data = isExpanded ? hedges : hedges.slice(0, 3);
    return <TerminatedHedgeTable history={history} data={data} dealId={dealId} />;
  }

  renderFooterButton = () => {
    const { isExpanded, currentState } = this.state;
    const { hedges, loans } = this.props;

    if (!currentState) return null;

    let buttonLabel = null;
    const iconName = isExpanded ? ICONS.UNFOLD_LESS : ICONS.UNFOLD_MORE;
    const switchKey = `${currentState}-${+isExpanded}`;
    const list = currentState === PAID_LOAN_STATE ? loans : hedges;
    const remaining = list.length - 3;

    // 0 denotes collapsed state
    // 1 denotes expanded state
    switch (switchKey) {
      case 'PAID_LOAN_STATE-0':
        buttonLabel = remaining <= 0 ? null : `Show ${remaining} more paid off loans`;
        break;

      case 'TERMINATED_HEDGE_STATE-0':
        buttonLabel = remaining <= 0 ? null : `Show ${remaining} more terminated hedges`;
        break;

      case 'PAID_LOAN_STATE-1':
      case 'TERMINATED_HEDGE_STATE-1':
        buttonLabel = 'Show less';
        break;

      default:
        buttonLabel = null;
        break;
    }

    return (
      <StyledModuleFooter>
        {buttonLabel && <ButtonText label={buttonLabel} iconName={iconName} onClick={this.toggleIsExpanded} />}
      </StyledModuleFooter>
    );
  }

  render() {
    const { loans, hedges, modulePermissions, paidLoanModulePaidLoanByDealLoading } = this.props;

    if (paidLoanModulePaidLoanByDealLoading) return null;

    if (!modulePermissions || !modulePermissions.canViewModule) return null;

    // if no hedges or loans dont show anything
    if ((!loans || !loans.length) && (!hedges || !hedges.length)) return null;

    return (
      <ModuleBase>
        <DefaultModuleHeader
          headerTitle={this.getTitle()}
          button={this.getHeaderButton()}
        />

        {this.renderPaidLoanTable()}
        {this.renderTerminatedHedgeTable()}
        {this.renderFooterButton()}
      </ModuleBase>
    );
  }
}

const mapStateToProps = ({ paidLoanModule, authentication, }) => ({
  ...paidLoanModule
  , ...paidLoanModule.paidLoanModuleReducer
  , modulePermissions: mapModulePermissions(authentication)
});

export default compose(
  withRouter
  , connect(mapStateToProps, {
    paidLoanGetDeal
    , paidLoanGetPaidLoansByDeal
    , paidLoanGetTerminatedHedgesByDeal
  })
)(PaidLoanModule);
